import { format, parse, subDays } from 'date-fns';
import Swal from 'sweetalert2';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const { toZonedTime } = require('date-fns-tz');

export const VANTAGE_PRO_UPGRADE_URL = 'https://www.sapaad.com/campaign/vantage-pro/';

export type PlanType = 'lite' | 'pro';
export type DateRangePlanLimitMode = 'view' | 'export';

export const LITE_MAX_LOOKBACK_YEARS = 2;
export const LITE_MAX_LOOKBACK_DAYS = 367 * LITE_MAX_LOOKBACK_YEARS;
export const LITE_MAX_SELECTION_DAYS = 367;
export const LITE_MAX_EXPORT_DAYS = 367;
export const PRO_MAX_LOOKBACK_DAYS = 365 * 5;

export const getExportPlanLimitNoticeMessage = (
  maxExportDays: number = LITE_MAX_EXPORT_DAYS
): string =>
  `Each export can include up to ${maxExportDays} days. Choose any date range within that limit — upgrade to Vantage Pro for extended date ranges.`;

export const normalizeCalendarDate = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const companyDateAtMidnight = (date: Date): Date => normalizeCalendarDate(date);

const currentCompanyDate = (timezoneOffset: string, referenceDate: Date = new Date()): Date => {
  const dateInTZ =
    timezoneOffset !== '+00:00' ? toZonedTime(referenceDate, timezoneOffset) : referenceDate;
  return companyDateAtMidnight(dateInTZ);
};

export const getEarliestAllowedDate = (
  maxLookbackDays: number,
  timezoneOffset: string = '+00:00',
  referenceDate: Date = new Date()
): Date => subDays(currentCompanyDate(timezoneOffset, referenceDate), maxLookbackDays);

export const isStartDateBeforePlanLimit = (
  startDate: Date,
  maxLookbackDays: number,
  timezoneOffset: string = '+00:00',
  referenceDate: Date = new Date()
): boolean => {
  const earliestAllowed = getEarliestAllowedDate(maxLookbackDays, timezoneOffset, referenceDate);
  const normalizedStart = companyDateAtMidnight(startDate);
  return normalizedStart < earliestAllowed;
};

type ViewPlanLimitNoticeOptions = {
  planMinDate?: Date;
  maxSelectionDays?: number;
};

export const formatPlanMinDateLabel = (planMinDate?: Date): string | null =>
  planMinDate ? format(planMinDate, 'd MMM yyyy') : null;

export const getViewPlanLimitNoticeContent = ({
  planMinDate,
  maxSelectionDays = LITE_MAX_SELECTION_DAYS,
}: ViewPlanLimitNoticeOptions = {}) => ({
  lookbackYears: LITE_MAX_LOOKBACK_YEARS,
  earliestDateLabel: formatPlanMinDateLabel(planMinDate),
  maxSelectionDays,
});

export const getViewPlanLimitNoticeMessage = (options: ViewPlanLimitNoticeOptions = {}): string => {
  const { lookbackYears, earliestDateLabel, maxSelectionDays } =
    getViewPlanLimitNoticeContent(options);
  const lookbackText = earliestDateLabel
    ? `the last ${lookbackYears} years (from ${earliestDateLabel} onwards)`
    : `the last ${lookbackYears} years`;

  return `On Lite, you can access data from ${lookbackText}. Each selection can cover up to ${maxSelectionDays} days at a time. Upgrade to Vantage Pro to access more historical data.`;
};

export const getViewPlanLimitNoticeHtml = (options: ViewPlanLimitNoticeOptions = {}): string => {
  const { lookbackYears, earliestDateLabel, maxSelectionDays } =
    getViewPlanLimitNoticeContent(options);
  const lookbackHtml = earliestDateLabel
    ? `the last <strong>${lookbackYears} years</strong> (from <strong>${earliestDateLabel}</strong> onwards)`
    : `the last <strong>${lookbackYears} years</strong>`;

  return `
    <p style="margin: 0 0 12px;">
      On Lite, you can access data from ${lookbackHtml}. Each selection can cover up to <strong>${maxSelectionDays} days</strong> at a time.
    </p>
    <p style="margin: 0; color: #5a6a7a;">
      Upgrade to <strong>Vantage Pro</strong> to access more historical data.
    </p>
  `;
};

export const getDateRangeLimitMessage = (
  _maxLookbackDays: number,
  planType: PlanType = 'lite',
  options: ViewPlanLimitNoticeOptions = {}
): string => {
  if (planType === 'lite') {
    return getViewPlanLimitNoticeMessage(options);
  }

  return 'Your Pro plan includes up to 5 years of reporting data. Please select a date within this range.';
};

export const isDateBeforePlanMin = (date: Date, planMinDate?: Date): boolean => {
  if (!planMinDate) {
    return false;
  }
  return normalizeCalendarDate(date) < normalizeCalendarDate(planMinDate);
};

export const isMonthBeforePlanMin = (monthDate: Date, planMinDate?: Date): boolean => {
  if (!planMinDate) {
    return false;
  }
  const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  return isDateBeforePlanMin(lastDayOfMonth, planMinDate);
};

export const isYearBeforePlanMin = (yearDate: Date, planMinDate?: Date): boolean => {
  if (!planMinDate) {
    return false;
  }
  const lastDayOfYear = new Date(yearDate.getFullYear(), 11, 31);
  return isDateBeforePlanMin(lastDayOfYear, planMinDate);
};

export const getSelectedRangeDays = (
  startDate: Date,
  endDate: Date,
  startTime: [number, number],
  endTime: [number, number]
): number => {
  const start = new Date(startDate);
  start.setHours(startTime[0], startTime[1], 0, 0);

  const end = new Date(endDate);
  end.setHours(endTime[0], endTime[1], 59, 0);

  const diffMs = end.getTime() - start.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

export const isExportRangeExceedsPlanLimit = (
  startDate: Date,
  endDate: Date,
  startTime: [number, number],
  endTime: [number, number],
  maxExportDays: number = LITE_MAX_EXPORT_DAYS
): boolean => getSelectedRangeDays(startDate, endDate, startTime, endTime) > maxExportDays;

export const isViewRangeExceedsPlanLimit = (
  startDate: Date,
  endDate: Date,
  startTime: [number, number],
  endTime: [number, number],
  maxSelectionDays: number = LITE_MAX_SELECTION_DAYS
): boolean => getSelectedRangeDays(startDate, endDate, startTime, endTime) > maxSelectionDays;

type ShowUpgradeDialogOptions = ViewPlanLimitNoticeOptions & {
  maxLookbackDays: number;
  planType?: PlanType;
};

export const showDateRangeLimitUpgradeDialog = ({
  maxLookbackDays: _maxLookbackDays,
  planType = 'lite',
  planMinDate,
  maxSelectionDays = LITE_MAX_SELECTION_DAYS,
}: ShowUpgradeDialogOptions) => {
  const isLitePlan = planType === 'lite';
  const earliestDateLabel = formatPlanMinDateLabel(planMinDate);

  Swal.fire({
    title: isLitePlan ? 'Unlock More Reporting History' : 'Date Range Limit Reached',
    html: `
      <div style="text-align: center; padding: 4px 0 8px; line-height: 1.55; font-size: 15px;">
        ${
          isLitePlan
            ? getViewPlanLimitNoticeHtml({ planMinDate, maxSelectionDays })
            : `<p style="margin: 0; color: #5a6a7a;">
                Your <strong>Pro</strong> plan includes up to <strong>5 years</strong> of reporting data.
                ${
                  earliestDateLabel
                    ? ` You can select dates from <strong>${earliestDateLabel}</strong> onwards.`
                    : ' Please select a date within this range.'
                }
              </p>`
        }
      </div>
    `,
    icon: 'info',
    showCancelButton: true,
    showConfirmButton: isLitePlan,
    confirmButtonText: isLitePlan ? 'Upgrade to Pro' : undefined,
    confirmButtonColor: '#3aa98d',
    cancelButtonText: 'Got it',
    customClass: {
      popup: 'date-range-upgrade-popup',
    },
  }).then((result) => {
    if (result.isConfirmed && isLitePlan) {
      window.open(VANTAGE_PRO_UPGRADE_URL, '_blank');
    }
  });
};

export const validateExportDateRange = (
  dates: [string, string],
  timeRange: [[number, number], [number, number]],
  planType: PlanType = 'lite',
  maxExportDays: number = LITE_MAX_EXPORT_DAYS
): boolean => {
  if (planType === 'pro') {
    return true;
  }

  const startDate = parse(dates[0], 'yyyy-MM-dd', new Date());
  const endDate = parse(dates[1], 'yyyy-MM-dd', new Date());

  if (isExportRangeExceedsPlanLimit(startDate, endDate, timeRange[0], timeRange[1], maxExportDays)) {
    showExportRangeLimitDialog({ maxExportDays, planType });
    return false;
  }

  return true;
};

export const showViewRangeLimitDialog = ({
  maxSelectionDays = LITE_MAX_SELECTION_DAYS,
  planType = 'lite',
  planMinDate,
}: ViewPlanLimitNoticeOptions & {
  planType?: PlanType;
}) => {
  const isLitePlan = planType === 'lite';

  Swal.fire({
    title: isLitePlan ? 'Date Range Too Long' : 'Date Range Limit Reached',
    html: `
      <div style="text-align: center; padding: 4px 0 8px; line-height: 1.55; font-size: 15px;">
        ${
          isLitePlan
            ? getViewPlanLimitNoticeHtml({ planMinDate, maxSelectionDays })
            : `<p style="margin: 0; color: #5a6a7a;">
                Your <strong>Pro</strong> plan includes up to <strong>5 years</strong> of reporting data. Please select a date within this range.
              </p>`
        }
      </div>
    `,
    icon: 'info',
    showCancelButton: true,
    showConfirmButton: isLitePlan,
    confirmButtonText: isLitePlan ? 'Upgrade to Pro' : undefined,
    confirmButtonColor: '#3aa98d',
    cancelButtonText: 'Got it',
  }).then((result) => {
    if (result.isConfirmed && isLitePlan) {
      window.open(VANTAGE_PRO_UPGRADE_URL, '_blank');
    }
  });
};

export const showExportRangeLimitDialog = ({
  maxExportDays: _maxExportDays = LITE_MAX_EXPORT_DAYS,
  planType = 'lite',
}: {
  maxExportDays?: number;
  planType?: PlanType;
}) => {
  const isLitePlan = planType === 'lite';

  Swal.fire({
    title: isLitePlan ? 'Export Limit Reached' : 'Date Range Limit Reached',
    html: `
      <div style="text-align: center; padding: 4px 0 8px; line-height: 1.55; font-size: 15px;">
        ${
          isLitePlan
            ? `<p style="margin: 0 0 12px;">
                Your current plan (<strong>Vantage Lite</strong>) allows up to <strong>${LITE_MAX_EXPORT_DAYS} days</strong> per export.
              </p>
              <p style="margin: 0; color: #5a6a7a;">
                Select a shorter date range, or upgrade to <strong>Vantage Pro</strong> for extended date ranges.
              </p>`
            : `<p style="margin: 0; color: #5a6a7a;">
                Your <strong>Pro</strong> plan includes up to <strong>5 years</strong> of reporting data. Please select a date within this range.
              </p>`
        }
      </div>
    `,
    icon: 'info',
    showCancelButton: true,
    showConfirmButton: isLitePlan,
    confirmButtonText: isLitePlan ? 'Upgrade to Pro' : undefined,
    confirmButtonColor: '#3aa98d',
    cancelButtonText: 'Got it',
  }).then((result) => {
    if (result.isConfirmed && isLitePlan) {
      window.open(VANTAGE_PRO_UPGRADE_URL, '_blank');
    }
  });
};
